/**
 * FieldActionChips - Icon buttons (token + expression + optional agent param)
 * at the right edge of a ParameterField.
 */

import { useCallback, useRef } from 'react';
import { makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components';
import {
  BracesVariableRegular,
  MathFormulaRegular,
} from '@fluentui/react-icons';
import { AgentParameterMenu } from '../fields/AgentParameterMenu';
import type { Token } from '@/types/valueSegment';

/** Stable marker class — referenced by ParameterField's parent-hover rule. Do not rename. */
export const FIELD_ACTION_CHIP_CLASS = 'otto-field-action-chip';

export interface FieldActionChipsProps {
  /** Parameter id this set of chips belongs to. */
  parameterId: string;
  /** Disabled / readonly fields hide the affordance. */
  disabled?: boolean;
  /** Optional override — usually defaults are fine. */
  onTokenClick?: (anchor: DOMRect) => void;
  onExpressionClick?: (anchor: DOMRect) => void;
  /** Show the agent parameter creation button (for tools inside agents) */
  showAgentParameterButton?: boolean;
  /** Field name for auto-naming new agent parameters */
  fieldName?: string;
  /** Field description from swagger — defaults the new parameter description */
  fieldDescription?: string;
  /** Field schema type from swagger (e.g., 'string', 'integer') — defaults the param type */
  fieldType?: string;
  /** Called when an agent parameter token should be inserted into the field */
  onAgentParameterSelect?: (token: Token) => void;
}

const useStyles = makeStyles({
  wrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0,
  },
  chip: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: '26px',
    height: '24px',
    padding: 0,
    margin: 0,
    border: 'none',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: 'transparent',
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: 1,
    cursor: 'pointer',
    opacity: 0.6,
    transitionProperty: 'background-color, color, opacity, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '::before': {
      content: '""',
      position: 'absolute',
      top: '-6px',
      right: '-4px',
      bottom: '-6px',
      left: '-4px',
    },
    ':hover': {
      opacity: 1,
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '1px',
      opacity: 1,
    },
    ':active': {
      transform: 'scale(0.94)',
      opacity: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': { transform: 'none' },
    },
    '@media (forced-colors: active)': {
      border: '1px solid CanvasText',
      backgroundColor: 'Canvas',
      color: 'CanvasText',
    },
  },
  chipToken: {
    color: tokens.colorBrandForeground1,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
      opacity: 1,
    },
  },
  chipExpr: {
    color: tokens.colorPaletteTealForeground2,
    ':hover': {
      backgroundColor: tokens.colorPaletteTealBackground2,
      opacity: 1,
    },
  },
  chipAgent: {
    color: tokens.colorPalettePurpleForeground2,
    ':hover': {
      backgroundColor: tokens.colorPalettePurpleBackground2,
      opacity: 1,
    },
  },
  chipIcon: {
    fontSize: '14px',
    lineHeight: 1,
  },
  chipDisabled: {
    opacity: 0.25,
    cursor: 'not-allowed',
    ':hover': {
      opacity: 0.25,
      backgroundColor: 'transparent',
    },
    ':active': {
      transform: 'none',
    },
  },
});

export const FieldActionChips = ({
  parameterId: _parameterId,
  disabled = false,
  onTokenClick,
  onExpressionClick,
  showAgentParameterButton = false,
  fieldName,
  fieldDescription,
  fieldType,
  onAgentParameterSelect,
}: FieldActionChipsProps) => {
  const styles = useStyles();
  const tokenChipRef = useRef<HTMLButtonElement>(null);
  const exprChipRef = useRef<HTMLButtonElement>(null);

  const handleTokenClick = useCallback(() => {
    if (disabled || !tokenChipRef.current || !onTokenClick) return;
    onTokenClick(tokenChipRef.current.getBoundingClientRect());
  }, [disabled, onTokenClick]);

  const handleExprClick = useCallback(() => {
    if (disabled || !exprChipRef.current || !onExpressionClick) return;
    onExpressionClick(exprChipRef.current.getBoundingClientRect());
  }, [disabled, onExpressionClick]);

  const tokenLabel = disabled ? 'Field is read-only.' : 'Add dynamic content';
  const exprLabel = disabled ? 'Field is read-only.' : 'Write expression';

  return (
    <div
      className={styles.wrapper}
      data-testid="field-action-chips"
      data-otto-field-chips=""
    >
      <Tooltip content={tokenLabel} relationship="label">
        <button
          ref={tokenChipRef}
          type="button"
          aria-label="Add dynamic content"
          disabled={disabled}
          onClick={handleTokenClick}
          className={mergeClasses(
            styles.chip,
            styles.chipToken,
            disabled && styles.chipDisabled,
            FIELD_ACTION_CHIP_CLASS
          )}
          data-testid="field-action-chip-token"
        >
          <BracesVariableRegular aria-hidden="true" className={styles.chipIcon} />
        </button>
      </Tooltip>
      <Tooltip content={exprLabel} relationship="label">
        <button
          ref={exprChipRef}
          type="button"
          aria-label="Write expression"
          disabled={disabled}
          onClick={handleExprClick}
          className={mergeClasses(
            styles.chip,
            styles.chipExpr,
            disabled && styles.chipDisabled,
            FIELD_ACTION_CHIP_CLASS
          )}
          data-testid="field-action-chip-expr"
        >
          <MathFormulaRegular aria-hidden="true" className={styles.chipIcon} />
        </button>
      </Tooltip>
      {showAgentParameterButton && (
        <AgentParameterMenu
          onSelect={onAgentParameterSelect ?? (() => {})}
          disabled={disabled}
          size="small"
          triggerClassName={mergeClasses(
            styles.chip,
            styles.chipAgent,
            disabled && styles.chipDisabled,
            FIELD_ACTION_CHIP_CLASS
          )}
          {...(fieldName ? { fieldName } : {})}
          {...(fieldDescription ? { fieldDescription } : {})}
          {...(fieldType ? { fieldType } : {})}
        />
      )}
    </div>
  );
};

FieldActionChips.displayName = 'FieldActionChips';
